import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loading,
  Progress,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock, Languages, Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useGenerateBulkTourTranslations } from '@/hooks/useGenerateBulkTourTranslations';
import {
  useTourTranslationJobSocket,
  type TranslationJobItem,
  type TranslationJobItemStatus,
  type TranslationJobState,
} from '@/hooks/useTourTranslationJobSocket';

interface Language {
  id: string;
  name: string;
  code: string;
  isDefault: boolean;
}

interface BulkTranslateTourDialogProps {
  tourId: string;
  open: boolean;
  onClose: () => void;
  /**
   * If provided, the dialog skips the language picker and immediately fires
   * a job for this single language. Used by the "catch up missing" flow on
   * the per-language translation detail page.
   */
  presetLanguage?: { id: string; name: string };
}

/**
 * Two-step modal that bulk-generates translations + voiceovers for every
 * attraction (and the theme variant used by the tour) in a tour.
 *
 * - Without `presetLanguage`: language picker → progress checklist.
 * - With `presetLanguage`: kicks off the job for that one language on open
 *   and renders only the progress checklist.
 */
export function BulkTranslateTourDialog({
  tourId,
  open,
  onClose,
  presetLanguage,
}: BulkTranslateTourDialogProps) {
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<string[]>([]);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const generateMutation = useGenerateBulkTourTranslations();
  const jobSocket = useTourTranslationJobSocket(activeJobId);
  const autoStartedRef = useRef(false);

  const { data: languagesResponse, isLoading: isLoadingLanguages } = useQuery({
    queryKey: ['Language'],
    queryFn: async () =>
      apiClient.get<{ data: Language[] }>('/languages?limit=100'),
    enabled: open && !activeJobId && !presetLanguage,
  });

  const languages = languagesResponse?.data ?? [];

  // Reset on open
  useEffect(() => {
    if (open) {
      setSelectedLanguageIds([]);
      setActiveJobId(null);
      autoStartedRef.current = false;
    }
  }, [open]);

  // Auto-start when a presetLanguage is provided.
  useEffect(() => {
    if (!open || !presetLanguage || autoStartedRef.current) return;
    autoStartedRef.current = true;
    generateMutation.mutate(
      { tourId, targetLanguageIds: [presetLanguage.id] },
      {
        onSuccess: ({ jobId }) => setActiveJobId(jobId),
      },
    );
  }, [open, presetLanguage, tourId, generateMutation]);

  // When job completes, refresh translation lists in the background so users
  // see new rows immediately if they close the dialog.
  useEffect(() => {
    const status = jobSocket.state?.status;
    if (status === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['Translation'] });
    }
  }, [jobSocket.state?.status, queryClient]);

  const handleStart = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedLanguageIds.length === 0) return;

    generateMutation.mutate(
      { tourId, targetLanguageIds: selectedLanguageIds },
      {
        onSuccess: ({ jobId }) => {
          setActiveJobId(jobId);
        },
      },
    );
  };

  const toggleLanguage = (languageId: string) => {
    setSelectedLanguageIds((prev) =>
      prev.includes(languageId)
        ? prev.filter((id) => id !== languageId)
        : [...prev, languageId],
    );
  };

  const showProgressView = Boolean(activeJobId) || Boolean(presetLanguage);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        {!showProgressView ? (
          <PickerView
            languages={languages}
            isLoadingLanguages={isLoadingLanguages}
            selectedLanguageIds={selectedLanguageIds}
            isStarting={generateMutation.isPending}
            onToggle={toggleLanguage}
            onCancel={onClose}
            onSubmit={handleStart}
          />
        ) : (
          <ProgressView
            job={jobSocket.state}
            isLoading={jobSocket.isConnecting || generateMutation.isPending}
            error={jobSocket.error}
            heading={
              presetLanguage
                ? `Catching up missing translations — ${presetLanguage.name}`
                : undefined
            }
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface PickerViewProps {
  languages: Language[];
  isLoadingLanguages: boolean;
  selectedLanguageIds: string[];
  isStarting: boolean;
  onToggle: (id: string) => void;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

function PickerView({
  languages,
  isLoadingLanguages,
  selectedLanguageIds,
  isStarting,
  onToggle,
  onCancel,
  onSubmit,
}: PickerViewProps) {
  return (
    <form onSubmit={onSubmit}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Languages className="h-5 w-5" />
          Generate Translations + Voiceovers
        </DialogTitle>
        <DialogDescription>
          For every attraction in this tour (and the theme variant used by
          each), generate translations into the selected languages and create
          voiceovers for the new translations. Existing translations are
          skipped.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-4">
        {isLoadingLanguages && <Loading text="Loading languages..." size="sm" />}

        {!isLoadingLanguages && languages.length === 0 && (
          <div className="text-center text-sm text-muted-foreground">
            No languages available
          </div>
        )}

        {!isLoadingLanguages && languages.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {languages.map((language) => (
              <label
                key={language.id}
                className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedLanguageIds.includes(language.id)}
                  onChange={() => onToggle(language.id)}
                  className="h-4 w-4"
                />
                <div className="flex-1">
                  <div className="font-medium">{language.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {language.code}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isStarting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={selectedLanguageIds.length === 0}
          loading={isStarting}
        >
          Start Generation
        </Button>
      </DialogFooter>
    </form>
  );
}

interface ProgressViewProps {
  job: TranslationJobState | null;
  isLoading: boolean;
  error: string | null;
  heading?: string;
  onClose: () => void;
}

function ProgressView({ job, isLoading, error, heading, onClose }: ProgressViewProps) {
  const stats = useMemo(() => computeStats(job?.items ?? []), [job?.items]);
  const isTerminal = job?.status === 'completed' || job?.status === 'failed';

  const grouped = useMemo(() => {
    if (!job) return { description: [], voiceover: [] };
    return {
      description: job.items.filter((i) => i.phase === 'description'),
      voiceover: job.items.filter((i) => i.phase === 'voiceover'),
    };
  }, [job]);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Languages className="h-5 w-5" />
          {heading ?? (isTerminal ? 'Generation Finished' : 'Generating Translations')}
        </DialogTitle>
        <DialogDescription>
          {!job && 'Starting…'}
          {job?.status === 'planning' && 'Planning work…'}
          {job?.status === 'running' &&
            `Processing ${stats.done + stats.failed + stats.skipped} of ${stats.total}`}
          {job?.status === 'completed' && stats.total === 0 &&
            'Nothing to do — every attraction in this tour is already translated for this language.'}
          {job?.status === 'completed' && stats.total > 0 &&
            `Done — ${stats.done} succeeded, ${stats.failed} failed, ${stats.skipped} skipped`}
          {job?.status === 'failed' && (job.error || 'Job failed')}
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-4">
        {isLoading && !job && <Loading text="Starting job..." size="sm" />}
        {error && (
          <div className="text-sm text-destructive">{error}</div>
        )}

        {job && (
          <>
            <div className="space-y-1">
              <Progress value={stats.percent} />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {stats.done + stats.failed + stats.skipped} / {stats.total}
                </span>
                <span>{stats.percent}%</span>
              </div>
            </div>

            {grouped.description.length > 0 && (
              <ItemSection
                title="Descriptions"
                items={grouped.description}
              />
            )}

            {grouped.voiceover.length > 0 && (
              <ItemSection title="Voiceovers" items={grouped.voiceover} />
            )}

            {job.status === 'running' && grouped.voiceover.length === 0 && (
              <div className="text-xs text-muted-foreground">
                Voiceover items will appear after descriptions complete.
              </div>
            )}
          </>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {isTerminal ? 'Close' : 'Run in background'}
        </Button>
      </DialogFooter>
    </>
  );
}

function ItemSection({
  title,
  items,
}: {
  title: string;
  items: TranslationJobItem[];
}) {
  return (
    <div className="rounded-md border">
      <div className="px-3 py-2 text-sm font-medium border-b bg-muted/30">
        {title}
        <span className="ml-2 text-xs text-muted-foreground font-normal">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>
      <ul className="divide-y">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex items-center gap-3 px-3 py-2 text-sm"
          >
            <StatusIcon status={item.status} />
            <div className="flex-1 min-w-0">
              <div className="truncate">{item.entityName}</div>
              {item.error && (
                <div className="text-xs text-destructive truncate">
                  {item.error}
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground shrink-0">
              {item.languageName}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusIcon({ status }: { status: TranslationJobItemStatus }) {
  switch (status) {
    case 'done':
      return <Check className="h-4 w-4 text-green-600 shrink-0" />;
    case 'running':
      return (
        <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />
      );
    case 'failed':
      return <X className="h-4 w-4 text-destructive shrink-0" />;
    case 'skipped':
      return (
        <span
          className="text-xs uppercase text-muted-foreground shrink-0"
          aria-label="skipped"
          title="skipped"
        >
          skip
        </span>
      );
    case 'queued':
    default:
      return <Clock className="h-4 w-4 text-muted-foreground shrink-0" />;
  }
}

function computeStats(items: TranslationJobItem[]) {
  const total = items.length;
  const done = items.filter((i) => i.status === 'done').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  const skipped = items.filter((i) => i.status === 'skipped').length;
  const settled = done + failed + skipped;
  const percent = total === 0 ? 0 : Math.round((settled / total) * 100);
  return { total, done, failed, skipped, percent };
}
